import { zodResolver } from '@hookform/resolvers/zod'
import * as Sentry from '@sentry/react'
import { Send } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'

import { Money } from '@/components/ds'
import { Loading } from '@/components/Loading'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import API from '@/lib/api'
import {
  ErrorResponse,
  ValidationError,
  ValidationErrorResponse,
} from '@/types/ErrorResponse'

const schema = z.object({
  subject: z.string(),
  recipient: z.string().email({
    message: 'error.email-invalid',
  }),
  cc: z.string(),
  message: z.string(),
})

type SendDocumentFormData = z.infer<typeof schema>

export function SendDocumentForm({
  type,
  defaultValues,
  symbol,
  uuid,
  summary,
}: {
  type: 'invoice' | 'order'
  defaultValues?: SendDocumentFormData
  symbol: string
  uuid: string
  /** Údaje pro náhled e-mailu */
  summary?: {
    supplierName?: string
    amount?: number
    currency?: string
    deadlineAt?: string
  }
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showCc, setShowCc] = useState(!!defaultValues?.cc)
  const navigate = useNavigate()
  const { t } = useTranslation(undefined, {
    keyPrefix: type,
  })

  const form = useForm<SendDocumentFormData>({
    resolver: zodResolver(schema),
    defaultValues,
  })

  const onSubmit = (data: SendDocumentFormData) => {
    setIsSubmitting(true)

    API.post(`/api/v2/${type}-submissions/${uuid}`, {
      subject: data.subject,
      recipient: data.recipient,
      cc: data.cc
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean),
      message: data.message,
    })
      .then(({ data }) => {
        if (data.status === 'success') {
          toast.success(t('send.success.title'))
        } else if (data.status === 'partial-success') {
          data.warnings.forEach(
            (warning: { code: string; message: string }) => {
              toast.warning(warning.message)
            },
          )
        } else {
          toast.error(t('send.error.title'))
        }

        navigate(`/${type}/${symbol}`)
      })
      .catch((error) => {
        console.error(JSON.stringify(error, null, 2))

        if (!error.response || !error.response.data) {
          Sentry.logger.error('No response data', {
            error: error,
            formValues: data,
          })
        }

        const { code, detail, status, title, type } = error.response
          .data as ErrorResponse

        console.log(code, detail, status, title, type)

        if (code === 40099) {
          ;(error.response.data as ValidationErrorResponse)[
            'invalid-params'
          ].map((param: ValidationError) => {
            form.setError(param.field as keyof SendDocumentFormData, {
              message: param.message,
            })
          })
        }

        toast.error(t('send.error.title'), {
          duration: 4000,
          closeButton: true,
        })
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const preview = form.watch()

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:gap-12"
      >
        <div className="flex flex-col gap-3">
          {/* Skladatel e-mailu: jeden rámeček, řádky Komu / Kopie / Předmět, pod nimi text zprávy */}
          <div className="overflow-hidden rounded-lg border border-input focus-within:ring-[3px] focus-within:ring-ring/30">
            <ComposerRow
              label="Komu"
              htmlFor="send-recipient"
              error={form.formState.errors.recipient?.message}
            >
              <input
                id="send-recipient"
                type="email"
                autoComplete="email"
                className="h-11 min-w-0 flex-1 bg-transparent text-sm outline-none"
                {...form.register('recipient')}
              />
              {!showCc && (
                <button
                  type="button"
                  onClick={() => setShowCc(true)}
                  className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  Kopie
                </button>
              )}
            </ComposerRow>
            {showCc && (
              <ComposerRow
                label="Kopie"
                htmlFor="send-cc"
                error={form.formState.errors.cc?.message}
              >
                <input
                  id="send-cc"
                  placeholder="Více adres oddělte čárkou"
                  className="h-11 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  autoFocus={!preview.cc}
                  {...form.register('cc')}
                />
              </ComposerRow>
            )}
            <ComposerRow
              label="Předmět"
              htmlFor="send-subject"
              error={form.formState.errors.subject?.message}
            >
              <input
                id="send-subject"
                className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium outline-none"
                {...form.register('subject')}
              />
            </ComposerRow>
            <label htmlFor="send-message" className="sr-only">
              {t('send.form.message.label')}
            </label>
            <textarea
              id="send-message"
              rows={10}
              className="field-sizing-content block min-h-56 w-full resize-none bg-transparent px-4 py-3.5 text-sm leading-relaxed outline-none"
              {...form.register('message')}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <p className="mr-auto text-xs text-muted-foreground">
              E-mail obsahuje odkaz na{' '}
              {type === 'invoice' ? 'fakturu' : 'výzvu k platbě'}, jak ukazuje
              náhled.
            </p>
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(`/${type}/${symbol}`)}
            >
              Zrušit
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Loading className="size-4" /> : <Send />}
              {t('send.form.submit.label')}
            </Button>
          </div>
        </div>

        <aside className="lg:sticky lg:top-6">
          <p className="mb-2 text-xs text-muted-foreground">Náhled e-mailu</p>
          <div className="overflow-hidden rounded-lg border">
            <div className="space-y-1 border-b bg-muted px-5 py-3 text-xs text-muted-foreground">
              <p>
                Komu:{' '}
                <span className="text-foreground">
                  {preview.recipient || '-'}
                </span>
                {preview.cc ? <span>, kopie {preview.cc}</span> : null}
              </p>
              <p>
                Předmět:{' '}
                <span className="text-foreground">
                  {preview.subject || '-'}
                </span>
              </p>
            </div>
            <div className="space-y-5 px-5 py-6 text-sm">
              <p className="whitespace-pre-line text-pretty">
                {preview.message}
              </p>
              {summary?.amount !== undefined && (
                <div className="rounded-lg border px-4 py-3">
                  <p className="text-xs text-muted-foreground">
                    {type === 'invoice' ? 'Faktura' : 'Výzva k platbě'} {symbol}
                  </p>
                  <Money
                    amount={summary.amount}
                    currency={summary.currency}
                    size="lg"
                  />
                  {summary.deadlineAt && (
                    <p className="text-xs text-muted-foreground">
                      Splatnost{' '}
                      {new Intl.DateTimeFormat('cs-CZ').format(
                        new Date(summary.deadlineAt),
                      )}
                    </p>
                  )}
                </div>
              )}
              <span className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground">
                Zobrazit doklad
              </span>
              {summary?.supplierName && (
                <p className="text-xs text-muted-foreground">
                  {summary.supplierName}
                </p>
              )}
            </div>
          </div>
        </aside>
      </form>
    </Form>
  )
}

/** Řádek skladatele e-mailu: popisek vlevo, pole vpravo, tenká linka pod ním. */
function ComposerRow({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string
  htmlFor: string
  error?: string
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <div className="border-b border-input/60">
      <div className="flex items-center gap-3 px-4">
        <label
          htmlFor={htmlFor}
          className="w-20 shrink-0 text-sm text-muted-foreground"
        >
          {label}
        </label>
        {children}
      </div>
      {error && <p className="px-4 pb-2 text-xs text-danger">{t(error)}</p>}
    </div>
  )
}
